/**
 * Announcements Carousel
 * Import and instantiate in home.js
 */
export default class Announcements {
	constructor(selector = '[data-carousel="announcements"]') {
		this.el = document.querySelector(selector);
		if (!this.el) return;

		this.track    = this.el.querySelector('[data-carousel-track]');
		this.slides   = [...this.el.querySelectorAll('[data-carousel-slide]')];
		this.bullets  = [...this.el.querySelectorAll('[data-carousel-goto]')];
		this.count    = this.slides.length;
		this.interval = parseInt(this.el.dataset.interval, 10) || 6000;
		this.current  = 0;
		this.timer    = null;
		this.paused   = false;

		if (!this.track || this.count === 0) return;

		this._bindEvents();
		this._startTimer();
	}

	// --- Core ---------------------------------------------------------------

	goTo(index) {
		const prev   = this.current;
		this.current = (index + this.count) % this.count;

		if (prev === this.current) return;

		this._animateHeight(this.slides[prev], this.slides[this.current]);
		this._deactivateSlide(prev);
		this._activateSlide(this.current);

		if (this.bullets.length) {
			this.bullets[prev].classList.remove('announcements__bullet--active');
			this.bullets[prev].setAttribute('aria-current', 'false');
			this.bullets[this.current].classList.add('announcements__bullet--active');
			this.bullets[this.current].setAttribute('aria-current', 'true');
		}
	}

	_animateHeight(fromSlide, toSlide) {
		const fromHeight = fromSlide.offsetHeight;

		// Briefly make the incoming slide visible (but transparent via CSS)
		// so we can measure its natural height
		toSlide.style.visibility = 'hidden';
		toSlide.style.display    = 'block';
		const toHeight = toSlide.offsetHeight;
		toSlide.style.visibility = '';
		toSlide.style.display    = '';

		// Pin the track at the current height then animate to the new one
		this.track.style.height     = `${fromHeight}px`;
		this.track.style.transition = 'height 0.4s ease';

		requestAnimationFrame(() => {
			this.track.style.height = `${toHeight}px`;
		});

		this.track.addEventListener('transitionend', () => {
			this.track.style.height     = '';
			this.track.style.transition = '';
		}, { once: true });
	}

	next() { this.goTo(this.current + 1); }
	prev() { this.goTo(this.current - 1); }

	// --- Slides -------------------------------------------------------------

	_activateSlide(i) {
		const slide = this.slides[i];
		slide.classList.add('announcements__slide--active');
		slide.removeAttribute('aria-hidden');
		if (slide.tagName === 'A') slide.setAttribute('tabindex', '0');
	}

	_deactivateSlide(i) {
		const slide = this.slides[i];
		slide.classList.remove('announcements__slide--active');
		slide.setAttribute('aria-hidden', 'true');
		if (slide.tagName === 'A') slide.setAttribute('tabindex', '-1');
	}

	// --- Timer --------------------------------------------------------------

	_startTimer() {
		if (this.count < 2) return;
		this.timer = setInterval(() => this.next(), this.interval);
	}

	_stopTimer() {
		clearInterval(this.timer);
		this.timer = null;
	}

	_pause() {
		this.paused = true;
		this._stopTimer();
	}

	_resume() {
		this.paused = false;
		this._startTimer();
	}

	// --- Events -------------------------------------------------------------

	_bindEvents() {
		this.bullets.forEach(btn => {
			btn.addEventListener('click', () => {
				this._stopTimer();
				this.goTo(parseInt(btn.getAttribute('data-carousel-goto'), 10));
				if (!this.paused) this._startTimer();
			});
		});

		this.el.addEventListener('mouseenter', () => this._pause());
		this.el.addEventListener('mouseleave', () => this._resume());
		this.el.addEventListener('focusin',    () => this._pause());
		this.el.addEventListener('focusout',   (e) => {
			if (!this.el.contains(e.relatedTarget)) this._resume();
		});

		this.el.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowRight') { this._stopTimer(); this.next(); if (!this.paused) this._startTimer(); }
			if (e.key === 'ArrowLeft')  { this._stopTimer(); this.prev(); if (!this.paused) this._startTimer(); }
		});

		let resizeTimer;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				const active = this.slides[this.current];
				this.track.style.height = `${active.offsetHeight}px`;
			}, 1500);
		});
	}
}
